import { useEffect, useState } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { atualizarCampoConfig, ouvirClinica } from '@/services/clinicasService';
import {
  EXPEDIENTE_PADRAO, expedienteDaClinica, metaDoDia, rotuloDoExpediente, validarFaixa,
} from '@/produtividade/expediente';
import { JANELA_PADRAO_DIAS } from '@/produtividade/acumulado';
import { normalizarTelefone } from '@/lib/telefone';

/**
 * ★★ WO lote 3a — DESEMPENHO: expediente, telefones da equipe e janela das taxas.
 *
 * Três ajustes que decidem como os Indicadores de Performance contam, e que até aqui eram
 * constante no código:
 *
 *  1. ★ **expediente** — quais dias têm meta e de que tamanho. ⚠ O sábado vale **metade**,
 *     derivado, não digitado: duplicar o campo convidaria os dois a divergirem.
 *  2. ★★ **telefones da equipe** — a trava anti-burla. Medido (`wo-medicao.md` §3): um
 *     "contato ativo" da Jéssica era o próprio dono, em conversa de teste. ⚠ Até hoje esta
 *     lista estava VAZIA em produção, e o filtro, embora ligado no código, não excluía
 *     ninguém — `usuarios` não tem campo `telefone`, só `ramal`.
 *  3. ★ **janela das taxas** — o acumulado. 90 por decisão do dono; com 30, o selo "base
 *     pequena" aparece em quase todo degrau.
 *
 * ★ Grava com `atualizarCampoConfig`, campo a campo: mandar o `config` inteiro apagaria
 * `funil`, `horarioAtendimento` e o que mais estiver lá.
 */

const ROTULO = {
  seg: 'Segunda', ter: 'Terça', qua: 'Quarta', qui: 'Quinta',
  sex: 'Sexta', sab: 'Sábado', dom: 'Domingo',
};
/** A ordem da semana de trabalho: segunda primeiro, domingo por último. */
const ORDEM = ['seg', 'ter', 'qua', 'qui', 'sex', 'sab', 'dom'];

export default function SecaoDesempenho() {
  const { clinicaId } = useAuth();
  const [config, setConfig] = useState(null);
  const [expediente, setExpediente] = useState(EXPEDIENTE_PADRAO);
  const [telefones, setTelefones] = useState('');
  const [janela, setJanela] = useState(String(JANELA_PADRAO_DIAS));
  const [salvando, setSalvando] = useState(null);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (!clinicaId) return undefined;
    return ouvirClinica(clinicaId, (c) => {
      setConfig(c?.config ?? null);
      setExpediente(expedienteDaClinica(c?.config));
      setTelefones((c?.config?.telefonesEquipe ?? []).join('\n'));
      setJanela(String(Number(c?.config?.janelaTaxasDias) || JANELA_PADRAO_DIAS));
    }, (e) => {
      console.error('[OdontoSDR] Falha ao ouvir a clínica:', e);
      setErro('Não foi possível carregar as configurações.');
    });
  }, [clinicaId]);

  async function gravar(campo, valor) {
    setSalvando(campo);
    setErro(null);
    try {
      await atualizarCampoConfig(clinicaId, campo, valor);
    } catch (e) {
      console.error('[OdontoSDR] Falha ao salvar:', e);
      setErro('Não foi possível salvar. Tente de novo.');
    } finally {
      setSalvando(null);
    }
  }

  const mudarDia = (dia, parte, valor) =>
    setExpediente((atual) => ({
      ...atual,
      [dia]: parte === 'fecha' ? null : { ...(atual[dia] ?? { de: '08:00', ate: '17:00' }), [parte]: valor },
    }));

  /**
   * ★★★ A-2 — O ALMOÇO ENTRA AQUI TAMBÉM, no MESMO formato do horário por pessoa.
   *
   * ⚠⚠ Dois formatos (um na clínica, outro na pessoa) seria a pior saída: ★ `expedienteDaPessoa`
   * cai no da clínica dia a dia, então os dois **têm** de ser lidos pela mesma função.
   *
   * ★ Campo de almoço vazio é removido antes de gravar — ⚠ `almocoDe: ''` no banco faria
   * `almocoDaFaixa` recusar o intervalo (o certo), mas deixaria lixo num documento que o
   * servidor vai ler no E7-b.
   */
  const limparFaixa = (faixa) => {
    if (!faixa) return null;
    const saida = { de: faixa.de, ate: faixa.ate };
    if (faixa.almocoDe && faixa.almocoAte) {
      saida.almocoDe = faixa.almocoDe;
      saida.almocoAte = faixa.almocoAte;
    }
    return saida;
  };

  const salvarExpediente = () => {
    // ⚠ Valida e NOMEIA o dia: "o almoço tem de ficar dentro do expediente" sem dizer onde
    // obrigaria o gestor a conferir sete linhas à mão.
    for (const dia of ORDEM) {
      const problema = validarFaixa(expediente[dia]);
      if (problema) {
        setErro(`${ROTULO[dia]}: ${problema}`);
        return Promise.resolve();
      }
    }
    setErro(null);
    return gravar('expediente', Object.fromEntries(ORDEM.map((d) => [d, limparFaixa(expediente[d])])));
  };

  const salvarTelefones = () => {
    /**
     * ★★ Guarda na forma CANÔNICA (`+55DD9XXXXXXXX`), pelo helper da casa.
     *
     * ⚠ O gestor cola como quiser — "(62) 90000-0001", "62900000001", "+5562900000001".
     * `normalizarTelefone` é a MESMA régua que o resto do sistema usa; um `replace(/\D/g)`
     * local aqui seria a terceira régua de telefone da casa, e foi exatamente a comparação
     * crua de dígitos que deixou a trava anti-burla quebrada no lote 2.
     * ★ Quem lê (`painelDoDia`) compara por `variantesTelefone`, então as duas grafias do
     * nono dígito casam dos dois lados.
     */
    const lista = [...new Set(
      telefones.split(/[\n,;]+/)
        .map((t) => normalizarTelefone(t) || String(t ?? '').replace(/\D/g, ''))
        .filter((t) => String(t).replace(/\D/g, '').length >= 10),
    )];
    setTelefones(lista.join('\n'));
    return gravar('telefonesEquipe', lista);
  };

  const salvarJanela = () => gravar('janelaTaxasDias', Math.max(7, Number(janela) || JANELA_PADRAO_DIAS));

  if (!config && !erro) return <p className="em-breve">Carregando…</p>;

  return (
    <>
      {erro && <p className="erro">{erro}</p>}

      <section className="secao">
        <h2 className="secao-titulo">Expediente</h2>
        <p className="secao-sub">
          Define quais dias têm meta e de que tamanho, nos Indicadores de Performance.{' '}
          <strong>No sábado a meta vale metade</strong>, arredondada para baixo — ela é
          derivada, não se digita em separado. Domingo não tem meta.
          {' '}
          {/* ★★ A-2: este horário passou a ser o PADRÃO. ⚠ Dizer isso aqui é o que evita o
              gestor procurar o horário da Talita nesta tela. */}
          Este é o horário <strong>padrão da clínica</strong>: quem tiver horário próprio em{' '}
          <strong>Equipe → Horário</strong> usa o dele.
          {' '}Durante o <strong>almoço o ritmo para</strong> — a meta do dia se distribui só
          pelas horas trabalhadas. Deixe o almoço em branco nos dias sem intervalo
          (o sábado, por exemplo).
        </p>
        <div className="tabela-envolve">
          <table className="tabela">
            <thead>
              <tr>
                <th>Dia</th><th>Abre</th><th>Fecha</th>
                <th>Almoço de</th><th>Almoço até</th>
                <th>Trabalha?</th><th>Meta de 7/dia vira</th>
              </tr>
            </thead>
            <tbody>
              {ORDEM.map((dia) => {
                const faixa = expediente[dia];
                const dataDeExemplo = { seg: '2026-09-07', ter: '2026-09-08', qua: '2026-09-09',
                  qui: '2026-09-10', sex: '2026-09-11', sab: '2026-09-05', dom: '2026-09-06' }[dia];
                return (
                  <tr key={dia}>
                    <td>{ROTULO[dia]}</td>
                    <td>
                      <input type="time" value={faixa?.de ?? ''} disabled={!faixa}
                        aria-label={`${ROTULO[dia]} — abre`}
                        onChange={(e) => mudarDia(dia, 'de', e.target.value)} />
                    </td>
                    <td>
                      <input type="time" value={faixa?.ate ?? ''} disabled={!faixa}
                        aria-label={`${ROTULO[dia]} — fecha`}
                        onChange={(e) => mudarDia(dia, 'ate', e.target.value)} />
                    </td>
                    <td>
                      <input type="time" value={faixa?.almocoDe ?? ''} disabled={!faixa}
                        aria-label={`${ROTULO[dia]} — almoço de`}
                        onChange={(e) => mudarDia(dia, 'almocoDe', e.target.value)} />
                    </td>
                    <td>
                      <input type="time" value={faixa?.almocoAte ?? ''} disabled={!faixa}
                        aria-label={`${ROTULO[dia]} — almoço até`}
                        onChange={(e) => mudarDia(dia, 'almocoAte', e.target.value)} />
                    </td>
                    <td>
                      <input type="checkbox" checked={Boolean(faixa)}
                        aria-label={`${ROTULO[dia]} — trabalha`}
                        onChange={(e) => (e.target.checked
                          ? mudarDia(dia, 'de', EXPEDIENTE_PADRAO[dia]?.de ?? '08:00')
                          : mudarDia(dia, 'fecha'))} />
                    </td>
                    {/* ★ O efeito da regra, à vista: quem configura vê o sábado virar 3.
                        ★★ E o rótulo que a equipe vai ler no cartão de ritmo, com o almoço. */}
                    <td className="campo-ajuda">
                      {metaDoDia(7, dataDeExemplo, expediente)}
                      {faixa && <div>{rotuloDoExpediente(dataDeExemplo, expediente)}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <button type="button" className="btn-primario" onClick={salvarExpediente} disabled={salvando === 'expediente'}>
          {salvando === 'expediente' ? 'Salvando…' : 'Salvar expediente'}
        </button>
      </section>

      <section className="secao">
        <h2 className="secao-titulo">Telefones da equipe</h2>
        <p className="secao-sub">
          Um por linha. Leads com estes telefones <strong>não entram nas contagens</strong> de
          produtividade — é a trava contra conversa de teste virar número de trabalho.{' '}
          {/* ⚠ A lacuna dita na tela: o gestor precisa saber que hoje ela está vazia. */}
          Enquanto a lista estiver vazia, nada é excluído.
        </p>
        <textarea rows={4} value={telefones} aria-label="Telefones da equipe"
          placeholder={'62900000001\n62900000002'}
          onChange={(e) => setTelefones(e.target.value)} />
        <button type="button" className="btn-primario" onClick={salvarTelefones} disabled={salvando === 'telefonesEquipe'}>
          {salvando === 'telefonesEquipe' ? 'Salvando…' : 'Salvar telefones'}
        </button>
      </section>

      <section className="secao">
        <h2 className="secao-titulo">Janela das taxas</h2>
        <p className="secao-sub">
          De quantos dias é o funil acumulado dos Indicadores. <strong>90 é o padrão</strong>:
          com 30, quase todo degrau fica com menos de 30 casos e ganha o selo “base pequena”,
          porque um caso a mais muda a porcentagem inteira.
        </p>
        <input type="number" min="7" max="365" value={janela} aria-label="Janela das taxas em dias"
          onChange={(e) => setJanela(e.target.value)} />
        <span className="campo-ajuda"> dias</span>
        <button type="button" className="btn-primario" onClick={salvarJanela} disabled={salvando === 'janelaTaxasDias'}>
          {salvando === 'janelaTaxasDias' ? 'Salvando…' : 'Salvar janela'}
        </button>
      </section>
    </>
  );
}
